import { useEffect, useState } from 'react';
import { subDays, format } from 'date-fns';
import DesktopLayout from '../../../components/layout/DesktopLayout';
import TrendChart from '../../../components/admin/analytics/TrendChart';
import TopProductsList from '../../../components/admin/analytics/TopProductsList';
import CategoryDistributionChart from '../../../components/admin/analytics/CategoryDistributionChart';
import SearchKeywordsList from '../../../components/admin/analytics/SearchKeywordsList';
import StockAlertsList from '../../../components/admin/analytics/StockAlertsList';
import * as analyticsApi from '../../../services/analyticsApi';
import type { ViewTrendData, TopProductData, CategoryDistributionData, SearchKeywordData, StockAlertData } from '../../../services/analyticsApi';
import './AdminAnalyticsDashboard.css';

const AdminAnalyticsDashboard = () => {
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [loading, setLoading] = useState(true);

  const [viewTrend, setViewTrend] = useState<ViewTrendData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProductData[]>([]);
  const [categoryDistribution, setCategoryDistribution] = useState<CategoryDistributionData[]>([]);
  const [searchKeywords, setSearchKeywords] = useState<SearchKeywordData[]>([]);
  const [stockAlerts, setStockAlerts] = useState<StockAlertData[]>([]);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subDays(new Date(), period === 'day' ? 7 : period === 'week' ? 30 : 90), 'yyyy-MM-dd');

      const [trendRes, topRes, categoryRes, keywordsRes, alertsRes] = await Promise.all([
        analyticsApi.getViewTrend({ period, startDate, endDate }),
        analyticsApi.getTopProducts({ limit: 10, period, startDate, endDate }),
        analyticsApi.getCategoryDistribution({ startDate, endDate }),
        analyticsApi.getSearchKeywords({ limit: 20, startDate, endDate }),
        analyticsApi.getStockAlerts({ threshold: 10 }),
      ]);

      if (trendRes.success) setViewTrend(trendRes.data);
      if (topRes.success) setTopProducts(topRes.data);
      if (categoryRes.success) setCategoryDistribution(categoryRes.data);
      if (keywordsRes.success) setSearchKeywords(keywordsRes.data);
      if (alertsRes.success) setStockAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DesktopLayout>
      <div className="admin-analytics-dashboard">
      <header className="admin-analytics-dashboard__header">
        <h1>統計分析儀表板</h1>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as 'day' | 'week' | 'month')}
          className="admin-analytics-dashboard__period-select"
        >
          <option value="day">最近 7 天</option>
          <option value="week">最近 30 天</option>
          <option value="month">最近 90 天</option>
        </select>
      </header>

      {loading ? (
        <div className="admin-analytics-dashboard__loading">載入中...</div>
      ) : (
        <div className="admin-analytics-dashboard__grid">
          <div className="admin-analytics-dashboard__section admin-analytics-dashboard__section--full">
            <TrendChart data={viewTrend} />
          </div>

          <div className="admin-analytics-dashboard__section">
            <TopProductsList data={topProducts} />
          </div>

          <div className="admin-analytics-dashboard__section">
            <CategoryDistributionChart data={categoryDistribution} />
          </div>

          <div className="admin-analytics-dashboard__section">
            <SearchKeywordsList data={searchKeywords} />
          </div>

          <div className="admin-analytics-dashboard__section">
            <StockAlertsList data={stockAlerts} />
          </div>
        </div>
      )}
    </div>
    </DesktopLayout>
  );
};

export default AdminAnalyticsDashboard;
